import { useEffect, useState, useCallback } from "react";
import Shell from "../components/Shell";
import {
  fetchAIBudget, updateAIBudgetCaps, setAIKillSwitch, setAIEnabled,
  setAICostAlerts, runAIAlertCheck, testAICostAlert,
} from "../lib/api";
import {
  PowerIcon, BellRingingIcon, RocketLaunchIcon, ProhibitIcon,
  CurrencyDollarIcon, CalendarBlankIcon, PulseIcon, ClockCounterClockwiseIcon,
} from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { toast } from "sonner";

const fmt = (n, d = 2) => `$${Number(n || 0).toFixed(d)}`;
const pct = (v, cap) => (cap > 0 ? Math.min(100, (Number(v || 0) / cap) * 100) : 0);
const ago = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function AIBudgetPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [daily, setDaily] = useState("");
  const [monthly, setMonthly] = useState("");
  const [runs, setRuns] = useState("");
  const [hardStop, setHardStop] = useState(true);

  const load = useCallback(async () => {
    try {
      const d = await fetchAIBudget();
      setData(d);
      setDaily(String(d.caps.daily_usd));
      setMonthly(String(d.caps.monthly_usd));
      setRuns(String(d.caps.daily_runs));
      setHardStop(!!d.caps.hard_stop);
    } catch {
      toast.error("Could not load AI budget");
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  if (!data) {
    return (
      <Shell title="AI Budget" subtitle="LLM cost governance — meter, cap and kill every AI call.">
        <div className="text-sm text-zinc-400" data-testid="ai-budget-loading">Loading AI Budget…</div>
      </Shell>
    );
  }

  const killed = data.kill_switch;
  const capsDirty =
    daily !== String(data.caps.daily_usd) ||
    monthly !== String(data.caps.monthly_usd) ||
    runs !== String(data.caps.daily_runs) ||
    hardStop !== data.caps.hard_stop;

  const saveCaps = () => {
    const d = parseFloat(daily), m = parseFloat(monthly), r = parseInt(runs, 10);
    if ([d, m].some((v) => !isFinite(v) || v < 0) || !isFinite(r) || r < 0) {
      toast.error("Caps must be non-negative numbers");
      return;
    }
    act(() => updateAIBudgetCaps({ ai_daily_cap_usd: d, ai_monthly_cap_usd: m, ai_daily_run_cap: r, ai_hard_stop: hardStop }), "Caps saved");
  };

  const maxAttr = Math.max(...data.attribution.map((a) => a.cost_usd), 0.0001);

  return (
    <Shell title="AI Budget" subtitle="LLM cost governance — meter, cap and kill every AI call (text + images).">
      {/* Kill switch */}
      <div
        data-testid="ai-killswitch-banner"
        className={`border-2 p-5 mb-4 flex flex-col sm:flex-row sm:items-center gap-4 ${killed ? "border-[#FF3B30] bg-[#FF3B30]/5" : "border-zinc-950 bg-white"}`}
      >
        <div className={`w-11 h-11 flex-shrink-0 flex items-center justify-center ${killed ? "bg-[#FF3B30] text-white" : "bg-zinc-950 text-white"}`}>
          <PowerIcon size={24} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black text-base">
            {killed ? "Global AI Kill Switch — ENGAGED" : "Global AI Kill Switch — armed & ready"}
          </div>
          <div className="text-sm text-zinc-500 mt-0.5">
            Emergency brake — stops <strong>every</strong> LLM call (classification, drafting and image generation).
            {killed ? " All AI is currently blocked." : ""}
          </div>
        </div>
        <button
          data-testid="ai-killswitch-btn"
          disabled={busy}
          onClick={() => act(() => setAIKillSwitch(!killed), killed ? "AI resumed" : "ALL AI stopped")}
          className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50 ${killed ? "bg-zinc-950 text-white hover:bg-zinc-800" : "bg-[#FF3B30] text-white hover:bg-[#e0342a]"}`}
        >
          <ProhibitIcon size={15} weight="bold" />
          {killed ? "Resume AI" : "Stop all AI"}
        </button>
      </div>

      {/* AI classifier automation */}
      <div
        data-testid="ai-classifier-banner"
        className={`border p-5 mb-4 flex flex-col sm:flex-row sm:items-center gap-4 ${data.ai_enabled ? "border-emerald-500 bg-emerald-50/40" : "border-zinc-200 bg-white"}`}
      >
        <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center ${data.ai_enabled ? "bg-emerald-500 text-white" : "bg-zinc-100 text-zinc-400"}`}>
          <RocketLaunchIcon size={20} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">
            AI lead classifier {data.ai_enabled ? "ACTIVE" : "OFF"}
          </div>
          <div className="text-sm text-zinc-500 mt-0.5">
            The main recurring auto-cost: scores borderline WhatsApp messages with gpt-4.1-mini. Turn off for zero background spend.
          </div>
        </div>
        <button
          data-testid="ai-classifier-toggle"
          disabled={busy || killed}
          onClick={() => act(() => setAIEnabled(!data.ai_enabled), !data.ai_enabled ? "Classifier enabled" : "Classifier disabled")}
          className={`relative inline-flex h-6 w-11 items-center transition-colors flex-shrink-0 ${data.ai_enabled ? "bg-emerald-500" : "bg-zinc-300"} disabled:opacity-50`}
          aria-label="Toggle AI classifier"
        >
          <span className={`inline-block h-4 w-4 transform bg-white transition ${data.ai_enabled ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>

      {/* Pushover alerts */}
      <div data-testid="ai-alerts-banner" className="border border-zinc-200 bg-white p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className={`w-2.5 h-2.5 rounded-full ${data.pushover_configured ? "bg-emerald-500" : "bg-zinc-300"}`} />
          <div>
            <div className="font-semibold text-sm flex items-center gap-2">
              <BellRingingIcon size={15} weight="fill" /> Pushover alerts
              <span className="text-xs text-zinc-400 font-normal">· {data.pushover_configured ? "configured" : "not configured"}</span>
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">
              End-of-day spend, end-of-month total, and burn anomalies (last-hour spend spikes).
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button data-testid="ai-run-check-btn" disabled={busy || !data.pushover_configured}
            onClick={() => act(() => runAIAlertCheck(), "Snapshot sent")}
            className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 bg-white hover:bg-zinc-50 disabled:opacity-40">
            Run alert check now
          </button>
          <button data-testid="ai-test-push-btn" disabled={busy || !data.pushover_configured}
            onClick={() => act(() => testAICostAlert(), "Test push sent")}
            className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 bg-white hover:bg-zinc-50 disabled:opacity-40">
            Send test push
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard testid="stat-today" icon={CurrencyDollarIcon} label="Today"
          value={fmt(data.today.spend_usd, 2)} cap={fmt(data.today.cap_usd, 2)} p={pct(data.today.spend_usd, data.today.cap_usd)} />
        <StatCard testid="stat-month" icon={CalendarBlankIcon} label="This month"
          value={fmt(data.month.spend_usd, 2)} cap={fmt(data.month.cap_usd, 2)} p={pct(data.month.spend_usd, data.month.cap_usd)} />
        <StatCard testid="stat-runs" icon={PulseIcon} label="Runs today"
          value={String(data.today.runs)} cap={String(data.today.runs_cap)} p={pct(data.today.runs, data.today.runs_cap)} plain />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Spending limits */}
        <div className="border border-zinc-200 bg-white p-5" data-testid="ai-caps-card">
          <div className="overline mb-1">Workspace caps</div>
          <div className="font-heading font-black text-lg mb-4">Spending limits</div>
          <CapField testid="cap-daily" label="Daily USD cap (use 0 for unlimited)" prefix="$" value={daily} onChange={setDaily} />
          <CapField testid="cap-monthly" label="Monthly USD cap (use 0 for unlimited)" prefix="$" value={monthly} onChange={setMonthly} />
          <CapField testid="cap-runs" label="Daily AI run count cap (use 0 for unlimited)" value={runs} onChange={setRuns} integer />
          <div className="flex items-center justify-between py-3 border-t border-zinc-100 mt-2">
            <div>
              <div className="text-sm font-semibold">Hard stop when caps hit</div>
              <div className="text-xs text-zinc-400">Block AI calls (429) when over budget. Off = soft warn only.</div>
            </div>
            <button data-testid="cap-hardstop-toggle" onClick={() => setHardStop((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center transition-colors ${hardStop ? "bg-zinc-950" : "bg-zinc-300"}`}>
              <span className={`inline-block h-4 w-4 transform bg-white transition ${hardStop ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
          <button data-testid="cap-save-btn" onClick={saveCaps} disabled={busy || !capsDirty}
            className="w-full mt-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider bg-zinc-950 text-white disabled:bg-zinc-300 disabled:cursor-not-allowed">
            Save caps
          </button>
        </div>

        {/* Cost attribution */}
        <div className="border border-zinc-200 bg-white p-5" data-testid="ai-attribution-card">
          <div className="overline mb-1">30-day spend by operation</div>
          <div className="font-heading font-black text-lg mb-4">Cost attribution</div>
          {data.attribution.length === 0 ? (
            <div className="text-sm text-zinc-400 py-8 text-center">No AI spend in the last 30 days.</div>
          ) : (
            <div className="space-y-3.5">
              {data.attribution.map((a) => (
                <div key={a.operation} data-testid={`attr-${a.operation}`}>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-sm font-medium">{a.label}</span>
                    <span className="text-xs font-mono-num text-zinc-500">{fmt(a.cost_usd, 4)} · {a.runs} run{a.runs === 1 ? "" : "s"}</span>
                  </div>
                  <div className="h-2 bg-zinc-100 overflow-hidden">
                    <div className={`h-full ${a.kind === "image" ? "bg-zinc-950" : "bg-blue-500"}`} style={{ width: `${Math.max(3, (a.cost_usd / maxAttr) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Burn trend */}
      <div className="border border-zinc-200 bg-white p-5 mb-6" data-testid="ai-burn-trend">
        <div className="overline mb-1">Daily spend (14d)</div>
        <div className="font-heading font-black text-lg mb-4">Burn trend</div>
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer minWidth={0}>
            <BarChart data={data.burn_trend} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={{ stroke: "#e4e4e7" }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} width={48}
                tickFormatter={(v) => `$${v.toFixed(2)}`} />
              <Tooltip cursor={{ fill: "#f4f4f5" }} formatter={(v) => [fmt(v, 4), "Spend"]}
                contentStyle={{ border: "1px solid #e4e4e7", borderRadius: 0, fontSize: 12 }} />
              <Bar dataKey="spend_usd" radius={[2, 2, 0, 0]}>
                {data.burn_trend.map((_, i) => <Cell key={i} fill="#18181b" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent calls */}
      <div className="border border-zinc-200 bg-white p-5 mb-4" data-testid="ai-recent-calls">
        <div className="overline mb-1 flex items-center gap-1.5"><ClockCounterClockwiseIcon size={13} weight="bold" /> Recent LLM calls</div>
        <div className="font-heading font-black text-lg mb-4">Last {data.recent.length} calls</div>
        {data.recent.length === 0 ? (
          <div className="text-sm text-zinc-400 py-6 text-center">No LLM calls recorded yet.</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {data.recent.map((r, i) => (
              <div key={i} data-testid="recent-call-row" className="flex items-center justify-between py-2.5 text-sm gap-3">
                <span className="font-medium text-blue-600 truncate flex-1 min-w-0">{r.label}</span>
                <span className="text-zinc-400 text-xs w-28 hidden sm:block truncate">{r.model}</span>
                <span className="text-zinc-500 font-mono-num text-xs w-20 text-right">{r.kind === "image" ? "1 img" : `${(r.tokens / 1000).toFixed(1)}K tok`}</span>
                <span className="font-mono-num text-xs w-20 text-right">{fmt(r.cost_usd, 4)}</span>
                <span className="text-zinc-400 text-xs w-16 text-right">{ago(r.at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cost model footer */}
      <div className="text-[11px] text-zinc-400 leading-relaxed border border-zinc-100 bg-zinc-50 p-3" data-testid="ai-cost-model">
        <strong className="text-zinc-500">Cost model:</strong> {data.cost_model}
      </div>
    </Shell>
  );
}

function StatCard({ icon: Icon, label, value, cap, p, plain, testid }) {
  const danger = p >= 100;
  const warn = p >= 80;
  const bar = danger ? "bg-[#FF3B30]" : warn ? "bg-amber-500" : "bg-zinc-950";
  return (
    <div className="border border-zinc-200 bg-white p-5" data-testid={testid}>
      <div className="overline mb-2 flex items-center gap-1.5"><Icon size={13} weight="bold" /> {label}</div>
      <div className="flex items-baseline gap-2">
        <span className={`font-heading font-black text-3xl font-mono-num ${danger ? "text-[#FF3B30]" : "text-zinc-950"}`}>{value}</span>
        <span className="text-sm text-zinc-400 font-mono-num">/ {cap}</span>
      </div>
      <div className="h-1.5 bg-zinc-100 overflow-hidden mt-3">
        <div className={`h-full ${bar} transition-all`} style={{ width: `${Math.max(2, p)}%` }} />
      </div>
      <div className="text-[11px] text-zinc-400 mt-1.5">{p.toFixed(0)}% used</div>
    </div>
  );
}

function CapField({ label, prefix, value, onChange, integer, testid }) {
  return (
    <div className="mb-3.5">
      <label className="block text-xs text-zinc-500 mb-1.5">{label}</label>
      <div className="flex items-center gap-1.5">
        {prefix && <span className="text-sm text-zinc-500">{prefix}</span>}
        <input
          data-testid={testid}
          type="number" min="0" step={integer ? "1" : "0.5"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-zinc-300 px-2.5 py-2 text-sm font-mono-num focus:outline-none focus:border-zinc-950"
        />
      </div>
    </div>
  );
}
